import React, { useEffect, useMemo, useState } from "react";
import MDSpinner from "react-md-spinner";
import { FaComments, FaUser, FaUsers } from "react-icons/fa";

import { fetchRadioGroups, fetchRadioMessages } from "./fieldAppApi";

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch (error) {
    return String(value);
  }
}

function FieldAppCommunicationPanel({ farmId, selection, t }) {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("all");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const [groupsData, messagesData] = await Promise.all([
          fetchRadioGroups(farmId),
          fetchRadioMessages(farmId, { limit: 100 }),
        ]);
        if (cancelled) return;
        setGroups(groupsData.results || []);
        setMessages(messagesData.results || []);
      } catch (loadError) {
        console.error(loadError);
        if (!cancelled) {
          setError(
            t(
              "Falha ao carregar mensagens de comunicação enviadas pelo app."
            )
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [farmId, t]);

  const filteredMessages = useMemo(() => {
    let rows = messages;

    if (selectedGroupId !== "all") {
      rows = rows.filter(
        (message) => String(message.groupId) === String(selectedGroupId)
      );
    }

    const operatorIds = selection && selection.operatorIds;
    if (operatorIds && operatorIds.length) {
      const idSet = new Set(operatorIds.map(Number));
      rows = rows.filter(
        (message) =>
          !message.senderOperatorId || idSet.has(Number(message.senderOperatorId))
      );
    }

    return rows;
  }, [messages, selectedGroupId, selection]);

  if (loading) {
    return (
      <div className="field-app-loading">
        <MDSpinner singleColor="#1ABB9C" size={36} />
      </div>
    );
  }

  return (
    <div className="field-app-communication">
      <p className="field-app-intro field-app-intro--compact">
        {t(
          "Mesma área Comunicação do app de campo: áudios e mensagens entre operadores e gestores. Os grupos são criados na plataforma; o operador envia e recebe pelo tablet."
        )}
      </p>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <div className="field-app-communication-toolbar">
        <label htmlFor="field-app-radio-group">{t("Canal")}</label>
        <select
          id="field-app-radio-group"
          className="form-control field-app-communication-select"
          value={selectedGroupId}
          onChange={(event) => setSelectedGroupId(event.target.value)}
        >
          <option value="all">{t("Todos os canais")}</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
      </div>

      {groups.length === 0 ? (
        <div className="field-app-empty-panel">
          {t(
            "Nenhum grupo de comunicação cadastrado. Crie grupos na plataforma para os operadores usarem no app."
          )}
        </div>
      ) : null}

      <div className="table-responsive">
        <table className="table table-striped jambo_table">
          <thead>
            <tr>
              <th>{t("Data")}</th>
              <th>{t("Canal")}</th>
              <th>{t("De")}</th>
              <th>{t("Para")}</th>
              <th>{t("Áudio")}</th>
              <th>{t("Transcrição")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredMessages.length === 0 ? (
              <tr>
                <td colSpan="6">
                  {t("Nenhuma mensagem de comunicação no período/filtro atual.")}
                </td>
              </tr>
            ) : (
              filteredMessages.map((message) => (
                <tr key={message.uuid || message.id}>
                  <td>{formatDateTime(message.timestamp || message.createdAt)}</td>
                  <td>{message.groupName || "—"}</td>
                  <td>
                    <FaUser style={{ marginRight: 6 }} />
                    {message.senderName || "—"}
                  </td>
                  <td>
                    {message.targetType === "directed" ? (
                      <>
                        <FaUser style={{ marginRight: 6 }} />
                        {message.directedToName || t("Operador")}
                      </>
                    ) : (
                      <>
                        <FaUsers style={{ marginRight: 6 }} />
                        {t("Equipe")}
                      </>
                    )}
                  </td>
                  <td>
                    {message.audioUrl ? (
                      <audio controls preload="none" className="field-app-audio">
                        <source src={message.audioUrl} />
                      </audio>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="field-app-communication-transcript">
                    {message.transcript || "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default FieldAppCommunicationPanel;
